import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppIcon from "components/ui/AppIcon.jsx";
import { AED } from "features/order-taking/pages/take-order/components/icons";

const statusStyles = {
  PENDING: {
    backgroundColor: "#FFF9E6",
    color: "#FF8C00",
  },
  COMPLETED: {
    backgroundColor: "#E6F7E6",
    color: "#22C55E",
  },
  COMPLETE: {
    backgroundColor: "#E6F7E6",
    color: "#22C55E",
  },
  ON_THE_WAY: {
    backgroundColor: "#E6F2FF",
    color: "#3B82F6",
  },
  SCHEDULED: {
    backgroundColor: "#E6F7E6",
    color: "#22C55E",
  },
  REJECTED: {
    backgroundColor: "#FFE6E6",
    color: "#EF4444",
  },
  PREPARING: {
    backgroundColor: "#f3e8ff",
    color: "#a855f7",
  },
  CANCELLED: {
    backgroundColor: "#FFE6E6",
    color: "#EF4444",
  },
  CANCELED: {
    backgroundColor: "#FFE6E6",
    color: "#EF4444",
  },
};

const getScheduledSlot = (preOrder) => {
  const isSameDay = moment
    .unix(preOrder?.preOrderSlotStartTime)
    .isSame(moment.unix(preOrder?.preOrderSlotEndTime), "day");
  if (isSameDay) {
    return (
      moment.unix(preOrder?.preOrderSlotStartTime).format("DD/MM/YYYY") +
      " (" +
      moment.unix(preOrder?.preOrderSlotStartTime).format("hh:mm A") +
      " - " +
      moment.unix(preOrder?.preOrderSlotEndTime).format("hh:mm A") +
      ")"
    );
  } else {
    return (
      moment
        .unix(preOrder?.preOrderSlotStartTime)
        .format("DD/MM/YYYY hh:mm A") +
      " - " +
      moment.unix(preOrder?.preOrderSlotEndTime).format("DD/MM/YYYY hh:mm A")
    );
  }
};

const OrdersTable = ({ data = [], onShowDetails, searchTerm = "" }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredOrders = useMemo(() => {
    const term = searchTerm?.trim();
    if (!term) return data;

    return data.filter((item) =>
      String(item?.orderNumber ?? "").includes(term)
    );
  }, [data, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const getStatusBadgeStyle = (status) => {
    const newStatus = status?.toUpperCase();
    return (
      statusStyles[newStatus] || {
        backgroundColor: "#FFF9E6",
        color: "#FF8C00",
      }
    );
  };

  const formatStatusLabel = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ON_THE_WAY") return "On the Way";
    if (normalized === "COMPLETE") return "Completed";
    return String(status || "")
      .toLowerCase()
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const columns = [
    {
      title: "Order #",
      dataIndex: "orderNumber",
    },
    {
      title: "Customer Name",
      dataIndex: "userInfo",
      render: (v) => v?.name,
    },

    {
      title: "Order Type",
      dataIndex: "orderType",
      render: (v) => v?.EN?.name,
    },
    {
      title: "Order Placed Time",
      dataIndex: "orderPlacedTime",
      render: (v) => moment.unix(v).format("DD-MM-Y hh:mm A"),
    },
    {
      title: "Order Scheduled Time",
      dataIndex: "preOrder",
      render: (v,row) => (
        <span className="flex justify-center items-center">
          {row?.isPreOrder && row?.preOrder ? getScheduledSlot(row?.preOrder) : "--"}
        </span>
      ),
    },
    {
      title: "Store name",
      dataIndex: "storeInfo",
      render: (v) => v?.baseLanguage?.name,
    },
    {
      title: "Amount",
      dataIndex: "totalOrderPrice",
      render: (v) => (
        <span className="flex justify-center items-center">
          {v ? (
            <div className="flex gap-2 items-center">
              <AED className="shrink-0" width={14} height={14} />{" "}
              {v?.toFixed(2)}
            </div>
          ) : (
            "--"
          )}
        </span>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "selectedPaymentMethod",
      render: (v) => v?.EN?.name || "",
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      render: (value) => (
        <span
          className="inline-flex min-w-[98px] items-center justify-center rounded-full px-4 py-2 text-sm font-medium"
          style={getStatusBadgeStyle(value)}
        >
          {formatStatusLabel(value)}
        </span>
      ),
    },
    {
      title: "Actions",
      fixed: "right",
      render: (_, row) => (
        <span onClick={() => onShowDetails?.(row)}>
          <AppIcon name="eye" className=" cursor-pointer" />
        </span>
      ),
    },
  ];

  return (
    <div className="mt-5 flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-3">
      <AppPaginatedTable
        columns={columns}
        data={filteredOrders}
        pageSize={pageSize}
        page={page}
        total={filteredOrders.length}
        onPageChange={(page, pageSize) => {
          setPage(page);
          setPageSize(pageSize);
        }}
        clientSidePagination={true}
        rowClassName="cursor-pointer"
        onRowClick={(row) => onShowDetails?.(row)}
      />
    </div>
  );
};

export default OrdersTable;
